// Enhanced Apollo hooks with Application Insights tracking
import { useMutation, useQuery, skipToken, SkipToken } from "@apollo/client/react";

import { OperationVariables, TypedDocumentNode } from "@apollo/client";

import { DocumentNode } from "graphql";
import { trackUserAction } from "../app-insights";

// Enhanced useQuery hook with user action tracking
export function useTrackedQuery<TData, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?:
    | (useQuery.Options<TData, TVariables> & {
        actionName?: string;
        userContext?: Record<string, unknown>;
      })
    | SkipToken,
) {
  const isSkipToken = options === skipToken;
  const { actionName, userContext, ...apolloOptions } = isSkipToken ? {} : options || {};

  const result = useQuery<TData, TVariables>(
    query,
    isSkipToken ? skipToken : (apolloOptions as useQuery.Options<TData, TVariables>),
  );

  // Track query execution if actionName is provided
  if (!isSkipToken && actionName && !result.loading && !result.previousData) {
    trackUserAction(actionName, {
      queryName: query.definitions[0]?.kind,
      hasData: !!result.data,
      hasError: !!result.error,
      variables: options && "variables" in options ? options.variables : undefined,
      ...userContext,
    });
  }

  return result;
}

// Enhanced useMutation hook with user action tracking
export function useTrackedMutation<TData, TVariables extends OperationVariables = OperationVariables>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: useMutation.Options<TData, TVariables> & {
    actionName?: string;
    userContext?: Record<string, unknown>;
  },
): ReturnType<typeof useMutation<TData, TVariables>> {
  const { actionName, userContext, ...apolloOptions } = options || {};

  const [mutationFn, result] = useMutation<TData, TVariables>(
    mutation,
    apolloOptions as useMutation.Options<TData, TVariables>,
  );

  // Enhanced mutation function with tracking
  const trackedMutationFn = async (...args: Parameters<typeof mutationFn>) => {
    const [mutationOptions] = args;
    if (actionName) {
      trackUserAction(`${actionName}_Started`, {
        mutationName: mutation.definitions[0]?.kind,
        variables: mutationOptions?.variables || apolloOptions?.variables,
        ...userContext,
      });
    }

    try {
      const mutationResult = await mutationFn(...args);

      if (actionName) {
        trackUserAction(`${actionName}_Success`, {
          mutationName: mutation.definitions[0]?.kind,
          hasData: !!mutationResult.data,
          ...userContext,
        });
      }

      return mutationResult;
    } catch (error) {
      if (actionName) {
        trackUserAction(`${actionName}_Error`, {
          mutationName: mutation.definitions[0]?.kind,
          error: error instanceof Error ? error.message : "Unknown error",
          ...userContext,
        });
      }
      throw error;
    }
  };

  return [trackedMutationFn, result] as const;
}
